import { ReactElement, useMemo } from "react";
import { parseTableChildren } from "./Collection/collectionParser";
import {
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderProps,
  TableColumnProps,
  TableBodyProps,
  TableRowProps,
  TableCellProps,
} from "./Collection/CompoundComponents";
import { useTableCollection } from "./Collection/useTableCollection";
import type {
  TableData,
  TableColumnData,
  TableRowData,
  TableCellData,
} from "./types";
import "./Table.css";

export type { TableColumnData, TableCellData, TableRowData, TableData };

export interface TableProps {
  children: [ReactElement<TableHeaderProps>, ReactElement<TableBodyProps>];
  /** Table name/caption for accessibility. Provides essential context for screen readers about table content. */
  name: string;
}

const TableComponent = ({ children, name }: TableProps) => {
  const tableData: TableData = useMemo(
    () => parseTableChildren(children),
    [children]
  );
  const collection = useTableCollection(tableData);

  return (
    <table
      className="proton-Table"
      role="table"
      aria-rowcount={collection.getTotalRowCount}
      aria-colcount={collection.columns.length}
      aria-label={name}
    >
      <caption className="proton-Table__caption">{name}</caption>

      {/* Header */}
      <thead
        className="proton-Table__rowGroup--header"
        role="rowgroup"
        style={collection.isHeaderHidden ? { display: "none" } : undefined}
      >
        <tr className="proton-Table__header-row" role="row" aria-rowindex={1}>
          {collection.columns.map((column, index) => (
            <th
              key={column.key}
              className="proton-Table__header"
              role="columnheader"
              scope="col"
              aria-colindex={index + 1}
              aria-sort={column.ariaSort}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>

      {/* Body */}
      <tbody className="proton-Table__body" role="rowgroup">
        {collection.rows.map((row, rowIndex) => (
          <tr
            key={row.key}
            className="proton-Table__row"
            role="row"
            aria-rowindex={collection.getRowAriaIndex(rowIndex)}
            onClick={row?.onClick}
            tabIndex={row?.onClick ? 0 : undefined}
            onKeyDown={
              row?.onClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      // Enable keyboard navigation
                      e.preventDefault();
                      row.onClick?.();
                    }
                  }
                : undefined
            }
          >
            {collection.columns.map((column, colIndex) => {
              const cell = row.cells.find((c) => c.columnKey === column.key);
              return (
                <td
                  key={column.key}
                  className="proton-Table__cell"
                  role="cell"
                  aria-colindex={colIndex + 1}
                  colSpan={cell?.colSpan}
                >
                  {cell?.value || ""}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * Base Table component that renders data in rows and columns using collections.
 *
 * - Built-in keyboard navigation for interactive rows.
 * - Full ARIA table semantics with proper roles, labels, and indices.
 *
 * API:
 *  - {@link TableProps}
 *
 * @example
 * ```tsx
 * <Table caption="Employee Directory">
 *   <Table.Header>
 *     <Table.Column id="name">Name</Table.Column>
 *   </Table.Header>
 *   <Table.Body>
 *     <Table.Row key="1">
 *       <Table.Cell>Joe</Table.Cell>
 *     </Table.Row>
 *   </Table.Body>
 * </Table>
 * ```
 */
const Table = Object.assign(TableComponent, {
  /** Table header: {@link TableHeaderProps} */
  Header: TableHeader,
  /** Table column: {@link TableColumnProps} */
  Column: TableColumn,
  /** Table body: {@link TableBodyProps} */
  Body: TableBody,
  /** Table row: {@link TableRowProps} */
  Row: TableRow,
  /** Table cell: {@link TableCellProps} */
  Cell: TableCell,
});

export { Table };
export type {
  TableHeaderProps,
  TableColumnProps,
  TableBodyProps,
  TableRowProps,
  TableCellProps,
};
